import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import CourseCard from '../components/CourseCard';
import { publicAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState, inputCls, labelCls, panelCls } from '../components/ui';

const LEVELS = ['', 'beginner', 'intermediate', 'advanced'];
const PRICES = [['', 'Any price'], ['free', 'Free'], ['paid', 'Paid']];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);

  const search = params.get('search') || '';
  const category = params.get('category') || '';
  const level = params.get('level') || '';
  const price = params.get('price') || '';
  const page = parseInt(params.get('page') || '1', 10);

  const setParam = useCallback((key, value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    publicAPI.categories().then(({ data: d }) => setCats(d)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      publicAPI.courses({ search, category, level, price, page, per_page: 12 })
        .then(({ data: d }) => setData(d))
        .catch(() => setData({ courses: [], total: 0 }));
    }, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [search, category, level, price, page]);

  const tops = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const pages = data ? Math.max(1, Math.ceil(data.total / 12)) : 1;

  // Windowed page list: 1 … (page-1) page (page+1) … last
  const pageList = useMemo(() => {
    const out = [];
    for (let i = 1; i <= pages; i += 1) {
      if (i === 1 || i === pages || Math.abs(i - page) <= 1) out.push(i);
      else if (out[out.length - 1] !== '…') out.push('…');
    }
    return out;
  }, [pages, page]);

  const catRow = (active) =>
    `flex items-center justify-between rounded px-2.5 py-1.5 text-left text-[12.5px] ${
      active ? 'bg-lms-raised font-medium text-lms-text' : 'text-lms-muted hover:bg-lms-raised hover:text-lms-text'
    }`;
  const subRow = (active) =>
    `flex items-center justify-between rounded py-1.5 pl-5 pr-2.5 text-left text-xs ${
      active ? 'bg-lms-raised font-medium text-lms-text' : 'text-lms-faint hover:bg-lms-raised hover:text-lms-text'
    }`;

  return (
    <div>
      {/* Title row */}
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-lms-text">{t('Courses')}</h1>
          <p className="mt-1 text-[12.5px] text-lms-muted">
            {t('Explore the full catalog and filter by category, level, and price.')}
          </p>
        </div>
        {data && <span className="text-[12.5px] text-lms-faint lms-num">{data.total} {t('results')}</span>}
      </div>

      {/* Filters + grid */}
      <div className="grid items-start gap-6 lg:grid-cols-[240px,1fr]">
        <aside className={`${panelCls} space-y-5 p-4`}>
          <div>
            <span className={labelCls}>{t('Search')}</span>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-lms-faint" />
              <input
                value={search}
                onChange={(e) => setParam('search', e.target.value)}
                placeholder={t('Search courses…')}
                className={`${inputCls} pl-8`}
              />
            </div>
          </div>

          <div>
            <span className={labelCls}>{t('Category')}</span>
            <div className="space-y-0.5">
              <button onClick={() => setParam('category', '')} className={`w-full ${catRow(!category)}`}>
                {t('All categories')}
              </button>
              {tops.map((c) => (
                <div key={c.id}>
                  <button onClick={() => setParam('category', c.id)} className={`w-full ${catRow(category === c.id)}`}>
                    <span>{c.name}</span>
                    <span className="text-lms-faint lms-num">{c.course_count}</span>
                  </button>
                  {cats.filter((s) => s.parent_id === c.id).map((s) => (
                    <button key={s.id} onClick={() => setParam('category', s.id)} className={`w-full ${subRow(category === s.id)}`}>
                      <span>{s.name}</span>
                      <span className="text-lms-faint lms-num">{s.course_count}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className={labelCls}>{t('Level')}</span>
            <select value={level} onChange={(e) => setParam('level', e.target.value)} className={inputCls}>
              {LEVELS.map((l) => <option key={l} value={l}>{l ? t(l) : t('Any level')}</option>)}
            </select>
          </div>

          <div>
            <span className={labelCls}>{t('Price')}</span>
            <select value={price} onChange={(e) => setParam('price', e.target.value)} className={inputCls}>
              {PRICES.map(([v, label]) => <option key={v} value={v}>{t(label)}</option>)}
            </select>
          </div>
        </aside>

        <div>
          {!data ? (
            <p className="text-sm text-lms-muted">{t('Loading…')}</p>
          ) : data.courses.length === 0 ? (
            <EmptyState>{t('No courses match these filters.')}</EmptyState>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {data.courses.map((c) => <CourseCard key={c.id} course={c} />)}
            </div>
          )}

          {pages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-1.5 text-[12.5px]">
              <button
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
                className="flex h-8 items-center rounded border border-lms-border bg-lms-surface px-3 text-lms-text disabled:text-lms-faint disabled:opacity-60"
              >
                {t('Previous')}
              </button>
              {pageList.map((p, i) =>
                p === '…' ? (
                  <span key={`e${i}`} className="px-1 text-lms-faint">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setParam('page', String(p))}
                    className={`flex h-8 w-8 items-center justify-center rounded lms-num ${
                      p === page
                        ? 'bg-lms-sidebar font-semibold text-white'
                        : 'border border-lms-border bg-lms-surface text-lms-text hover:border-[var(--lms-hover-border)]'
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                disabled={page >= pages}
                onClick={() => setParam('page', String(page + 1))}
                className="flex h-8 items-center rounded border border-lms-border bg-lms-surface px-3 text-lms-text disabled:text-lms-faint disabled:opacity-60"
              >
                {t('Next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
